import { Box, Button, Divider, ListItemText, Menu, MenuItem, MenuList, Tooltip, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import useSessionSaveSlotScope from '../../../hooks/ui/useSessionSaveSlotScope';
import { applicationSlice } from '../../../redux/ApplicationSlice';
import { projectSlice } from '../../../redux/ProjectSlice';
import { useAppSelector } from '../../../redux/ReduxHooks';
import { RootState } from '../../../redux/Store';
import { ApplicationActions, RobotActions, SLOT_NAMES, slotNumberFromSlotName } from '../../../utils/constants';
import {
  getSaveSlotDisplayProgramName,
  getValidSaveSlotNumber,
  isSaveSlotEmpty,
  parseProjectLastModifiedToUtcMs,
  saveSlotNeedsReplaceConfirmation,
} from '../../../utils/saveSlotProjectId';
import SaveSlotReplaceConfirmDialog from '../../Dialogs/Project/SaveSlotReplaceConfirmDialog';
import MainProgramIcon from '../../Icons/Program/main-program.svg?react';
import UserProgramIcon from '../../Icons/Program/user-program.svg?react';

interface SlotButtonProps {
  disabled?: boolean;
  openSlotMenuSignal?: number;
  onSlotMenuDismissed?: () => void;
  onSlotSelected?: (slot: number) => void;
  showForcedDeployReplaceHint?: boolean;
}

const SlotButton = ({
  disabled = false,
  openSlotMenuSignal = 0,
  onSlotMenuDismissed,
  onSlotSelected,
  showForcedDeployReplaceHint = false,
}: SlotButtonProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const slotButtonRef = React.useRef<HTMLButtonElement | null>(null);
  const consumedOpenSlotMenuSignalRef = React.useRef(0);
  const [slotMenuAnchor, setSlotMenuAnchor] = React.useState<null | HTMLElement>(null);
  const [replaceConfirmOpen, setReplaceConfirmOpen] = React.useState(false);
  const [pendingSlot, setPendingSlot] = React.useState<string | null>(null);
  const [replaceConfirmMetadataUnverified, setReplaceConfirmMetadataUnverified] = React.useState(false);
  const loadedProject = useSelector((state: RootState) => state.project.loadedProject);
  const sessionSaveSlot = useAppSelector((state) => state.project.sessionSaveSlot);
  const loadedProjectId = loadedProject?.id;
  const loadedProjectLastModifiedUtcMs = parseProjectLastModifiedToUtcMs(loadedProject?.lastModified);
  const driveUnitStorage = useSelector((state: RootState) => state.robotData.slotStorage ?? {});
  const slotStorageReady = useSelector((state: RootState) => state.robotData.slotStorageReady);
  const slotStorageFresh = useSelector((state: RootState) => state.robotData.slotStorageFresh);
  const robotState = useSelector((state: RootState) => state.robot.robotState);
  const userAgentInfo = useSelector((state: RootState) => state.application.userAgentInfo);
  const openDrawer = useSelector((state: RootState) => state.project.openDrawer);
  const { BLUETOOTH_WARNING_DIALOG } = ApplicationActions.Dialogs;
  const slotStorageQuerying = robotState === RobotActions.STATE.CONNECTED && !slotStorageReady;
  const slotStorageUnverified = robotState === RobotActions.STATE.CONNECTED && slotStorageReady && !slotStorageFresh;

  useSessionSaveSlotScope();

  React.useEffect(() => {
    if (openSlotMenuSignal <= 0 || openSlotMenuSignal === consumedOpenSlotMenuSignalRef.current) {
      return;
    }

    consumedOpenSlotMenuSignalRef.current = openSlotMenuSignal;

    if (disabled) {
      onSlotMenuDismissed?.();
      return;
    }

    if (slotButtonRef.current) {
      setSlotMenuAnchor(slotButtonRef.current);
    }
  }, [disabled, onSlotMenuDismissed, openSlotMenuSignal]);

  React.useEffect(() => {
    if (disabled && slotMenuAnchor !== null) {
      setSlotMenuAnchor(null);
      onSlotMenuDismissed?.();
    }
  }, [disabled, onSlotMenuDismissed, slotMenuAnchor]);

  const getSlotTitle = (slotKey: string) => {
    const slot = driveUnitStorage[slotKey];
    const slotNumber = String(slotNumberFromSlotName(slotKey));
    const slotName = slotStorageQuerying
      ? t('slot.checking', 'Checking...')
      : slot && !isSaveSlotEmpty(slot) && slot.programTitle
      ? getSaveSlotDisplayProgramName(slot.programTitle)
      : t('slot.empty', 'Empty');
    return t('slot.slotLabel', 'Slot {{slotNumber}}: {{slotName}}', { slotName, slotNumber });
  };

  const selectedSlotNumber = getValidSaveSlotNumber(sessionSaveSlot);
  const selectedSlotLabel =
    selectedSlotNumber === null
      ? t('slot.setSlot', 'Set Slot')
      : t('slot.selectedSlot', 'Slot {{slotNumber}}', { slotNumber: String(selectedSlotNumber) });
  const saveSlotTooltip = t('primaryBar.tooltip.saveSlot', 'Program save location');
  const saveSlotButtonLabel = `${saveSlotTooltip}: ${selectedSlotLabel}`;

  const handleSlotMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled) return;

    if (robotState === RobotActions.STATE.CONNECTED) {
      setSlotMenuAnchor(event.currentTarget);
    } else if (userAgentInfo?.isBrowserSupported) {
      const connectionDrawerAction = openDrawer === ApplicationActions.Drawer.CONNECTION_DRAWER ? null : ApplicationActions.Drawer.CONNECTION_DRAWER;
      dispatch(projectSlice.actions.onOpenDrawer(connectionDrawerAction));
    } else {
      dispatch(applicationSlice.actions.onOpenDialog(BLUETOOTH_WARNING_DIALOG));
    }
  };

  const handleSlotMenuClose = () => {
    setSlotMenuAnchor(null);
    onSlotMenuDismissed?.();
  };

  const applySelectedSlot = (slot: number) => {
    dispatch(projectSlice.actions.setSelectedSaveSlot(slot));
    setSlotMenuAnchor(null);
    onSlotSelected?.(slot);
  };

  const handleSlotSelect = (slotKey: string) => {
    if (disabled || slotStorageQuerying) return;

    const slotInfo = driveUnitStorage[slotKey];
    if (slotStorageUnverified || saveSlotNeedsReplaceConfirmation(slotInfo, loadedProjectId, loadedProjectLastModifiedUtcMs)) {
      setPendingSlot(slotKey);
      setReplaceConfirmMetadataUnverified(slotStorageUnverified);
      setReplaceConfirmOpen(true);
    } else {
      applySelectedSlot(slotNumberFromSlotName(slotKey));
    }
  };

  const handleReplaceConfirm = () => {
    if (pendingSlot !== null) applySelectedSlot(slotNumberFromSlotName(pendingSlot));
    setReplaceConfirmOpen(false);
    setPendingSlot(null);
    setReplaceConfirmMetadataUnverified(false);
  };

  const handleReplaceCancel = () => {
    setReplaceConfirmOpen(false);
    setPendingSlot(null);
    setReplaceConfirmMetadataUnverified(false);
  };

  return (
    <>
      <SaveSlotReplaceConfirmDialog
        open={replaceConfirmOpen}
        onCancel={handleReplaceCancel}
        onReplace={handleReplaceConfirm}
        showForcedDeployHint={showForcedDeployReplaceHint}
        metadataUnverified={replaceConfirmMetadataUnverified}
      />
      {/* Save Slot Button */}
      <Tooltip title={saveSlotButtonLabel}>
        <span>
          <Button
            ref={slotButtonRef}
            size="small"
            variant="text"
            onClick={handleSlotMenuClick}
            disabled={disabled}
            startIcon={<Box component={MainProgramIcon} aria-hidden="true" sx={{ width: 20, height: 20, display: 'block', flexShrink: 0 }} />}
            aria-label={saveSlotButtonLabel}
            sx={(theme) => ({
              height: 34,
              minWidth: selectedSlotNumber === null ? 108 : 86,
              px: 1.25,
              borderRadius: 1,
              bgcolor: theme.vars?.palette?.accent?.opaque || theme.palette.accent?.opaque || theme.palette.action.selected,
              color: theme.vars?.palette?.text?.primary || theme.palette.text.primary,
              fontSize: 13,
              fontWeight: 600,
              textTransform: 'none',
              boxShadow: 'none',
              '& .MuiButton-startIcon': { mr: 0.75 },
              '&:hover': {
                bgcolor: theme.vars?.palette?.action?.hover || theme.palette.action.hover,
                boxShadow: 'none',
              },
              '&.Mui-disabled': {
                bgcolor: theme.vars?.palette?.action?.disabledBackground || theme.palette.action.disabledBackground,
                color: theme.vars?.palette?.text?.disabled || theme.palette.text.disabled,
              },
            })}
          >
            {selectedSlotLabel}
          </Button>
        </span>
      </Tooltip>

      {/* Slot Menu */}
      <Menu
        anchorEl={slotMenuAnchor}
        open={Boolean(slotMenuAnchor)}
        onClose={handleSlotMenuClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
      >
        <MenuList sx={{ minWidth: 260, maxWidth: 320 }}>
          {/* Menu Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1 }}>
            <Box component={UserProgramIcon} aria-hidden="true" sx={{ width: 22, height: 22, color: 'text.primary', flexShrink: 0 }} />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {t('slot.menuTitle', 'User Program Save Location')}
            </Typography>
          </Box>
          <Divider sx={{ my: 0.5 }} />

          {SLOT_NAMES.map((slotKey) => {
            const slot = driveUnitStorage[slotKey];
            const slotNumber = String(slotNumberFromSlotName(slotKey));
            const isSelected = sessionSaveSlot?.toString() === slotNumber;
            const slotMenuItem = (
              <MenuItem
                key={slotKey}
                disabled={slotStorageQuerying}
                onClick={() => handleSlotSelect(slotKey)}
                aria-current={isSelected ? 'true' : undefined}
                sx={(theme) => ({
                  backgroundColor: isSelected ? theme.vars?.palette?.accent?.opaque : 'transparent',
                  borderRight: isSelected ? `3px solid ${theme.vars?.palette?.accent?.main}` : '3px solid transparent',
                  borderRadius: 0,
                  '&:hover': {
                    backgroundColor: isSelected ? theme.vars?.palette?.accent?.opaque : theme.vars?.palette?.action?.hover,
                  },
                })}
              >
                <Box
                  component={MainProgramIcon}
                  aria-hidden="true"
                  sx={{
                    width: 22,
                    height: 22,
                    color: isSaveSlotEmpty(slot) ? 'text.secondary' : 'text.primary',
                    flexShrink: 0,
                    mr: 1.5,
                  }}
                />
                <ListItemText>
                  <Typography noWrap sx={{ maxWidth: 240 }}>
                    {getSlotTitle(slotKey)}
                  </Typography>
                </ListItemText>
              </MenuItem>
            );

            if (!slotStorageQuerying) {
              return slotMenuItem;
            }

            return (
              <Tooltip key={slotKey} title={t('slot.checkingTooltip', 'Checking robot slots')}>
                <span>{slotMenuItem}</span>
              </Tooltip>
            );
          })}
        </MenuList>
      </Menu>
    </>
  );
};

export default SlotButton;
